import {
  einheitFuerPhase,
  type EssenceEmpfehlung,
  type ProduktKey,
} from "@nl/shared"

import {
  useBereichFreigabeStatus,
  useEinheitFreigabeStatus,
} from "@/lib/einheit-freigabe"
import { useProduktFreigabeStatus } from "@/lib/produkt-freigabe"
import { useLearningEssence } from "@/lib/queries"

export type EmpfehlungsZiel = {
  empfehlung: EssenceEmpfehlung
  to: "/durchlauf" | "/produkttraining" | "/pruefung"
  search: Record<string, string | boolean>
  image: string
}

function ziel(empfehlung: EssenceEmpfehlung): EmpfehlungsZiel {
  if (empfehlung.art === "produkttraining" && empfehlung.produkt) {
    return {
      empfehlung,
      to: "/produkttraining",
      search: { produkt: empfehlung.produkt },
      image: `/mediathek/produkt-${empfehlung.produkt}.jpg`,
    }
  }
  if (empfehlung.art === "gespraechstraining" && empfehlung.phase) {
    return {
      empfehlung,
      to: "/durchlauf",
      search: { schritt: "persona", phase: empfehlung.phase },
      image: `/phasen/${empfehlung.phase}.jpg`,
    }
  }
  return {
    empfehlung,
    to: "/pruefung",
    search: { gemischt: true },
    image: "/mediathek/coach-gespraech-weit.jpg",
  }
}

/**
 * The training the coach recommended at the end of the onboarding, with where
 * it starts. Undefined while nothing is recommended or while the Sparkasse has
 * the area, unit or product locked, so no tile leads into a lock.
 */
export function useEssenceEmpfehlung(): EmpfehlungsZiel | undefined {
  const { data } = useLearningEssence()
  const bereiche = useBereichFreigabeStatus()
  const einheiten = useEinheitFreigabeStatus()
  const produkte = useProduktFreigabeStatus()

  const empfehlung = data?.essence?.empfehlung
  if (!empfehlung) return undefined
  if (empfehlung.art === "produkttraining") {
    if (
      bereiche.istGesperrt("produkttraining") ||
      !empfehlung.produkt ||
      produkte.istGesperrt(empfehlung.produkt as ProduktKey)
    )
      return undefined
  } else if (empfehlung.art === "gespraechstraining") {
    const einheit = empfehlung.phase && einheitFuerPhase(empfehlung.phase)
    if (bereiche.istGesperrt("gespraechstraining") || !einheit || !einheiten.istOffen(einheit))
      return undefined
  } else if (bereiche.istGesperrt("pruefungsvorbereitung")) {
    return undefined
  }
  return ziel(empfehlung)
}
